import { Component, computed, DestroyRef, effect, inject, signal, untracked, WritableSignal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { NotificationAlarmNotificationTemplateComponent } from '@lib/notification/feature-alarm-notification-template';
import { NotificationCategory, NotificationModel, NotificationGroupModel } from '@lib/notification/models';
import { FeatureToggleService } from '@lib/feature-toggle/data-access';
import { FeatureToggleType } from '@lib/feature-toggle/models';
import { NotificationService } from '@lib/notification/data-access';
import { EnvironmentService, ToastMessageService } from '@lib/shared/data-access';
import { finalize } from 'rxjs';
import { IncidentNotificationTemplateComponent } from '@lib/notification/feature-incident-review-notification-template'
import { LinkNotificationTemplateComponent } from '@lib/notification/feature-link-notification-template';
import { NotificationIncidentAssignmentNotificationTemplateComponent } from '@lib/notification/feature-incident-assignment-notification-template';
import { IntegrationNotificationTemplateComponent } from '@lib/notification/feature-integration-notification-template'
import { SystemNotificationTemplateComponent } from '@lib/notification/feature-system-notification-template';
import { AuthService } from '@lib/auth/data-access';
import { SignalRService } from '@lib/signalr/data-access';
import {
  DaisyDrawerComponent,
  DaisyPaginationComponent,
  DaisyToggleComponent,
  DaisyTooltipDirective,
} from '@pathfinder/daisy-controls/ui';

/** One category tab: its value (the key the list @switch reads), title, icon and unread count. */
interface NotificationTab {
  value: string;
  title: string;
  icon: string;
  count: string;
  dataId: string;
}

@Component({
  selector: 'lib-notification-feature-bell',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    DaisyDrawerComponent,
    DaisyPaginationComponent,
    DaisyToggleComponent,
    DaisyTooltipDirective,
    IncidentNotificationTemplateComponent,
    NotificationIncidentAssignmentNotificationTemplateComponent,
    NotificationAlarmNotificationTemplateComponent,
    LinkNotificationTemplateComponent,
    SystemNotificationTemplateComponent,
    IntegrationNotificationTemplateComponent,
],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
})
export class NotificationBellComponent {
  // inject
  public notificationService = inject(NotificationService);
  public signalRService = inject(SignalRService);
  private toastMessageService = inject(ToastMessageService);
  private featureToggleService = inject(FeatureToggleService);
  public environmentService = inject(EnvironmentService);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  // public
  public isNotificationFeatureEnabled = computed(() => { return this.featureToggleService.tenantFeaturesComputedSignal().some(feature => feature.id === FeatureToggleType.Notifications && feature.isEnabled) })
  public visible = this.notificationService.notificationSidebarVisibleComputedSignal;
  public notifications: NotificationModel[] = [];
  public groupedReviewNotifications: NotificationGroupModel[] = [];
  public groupedAssignmentNotifications: NotificationGroupModel[] = [];
  public groupedAlarmNotifications: NotificationGroupModel[] = [];
  public reportNotifications: NotificationModel[] = [];
  public integrationNotifications: NotificationModel[] = [];

  public notificationsCountComputed = computed(() => this.getNotificationCount(this.notificationService.notificationsComputedSignal()));
  public reviewNotificationCountComputed = computed(() => this.getNotificationCount(this.notificationService.notificationsComputedSignal().filter(x => x.category === NotificationCategory.NarrativeReviews || x.category === NotificationCategory.IncidentReviews)));
  public reportNotificationCountComputed = computed(() => this.getNotificationCount(this.notificationService.notificationsComputedSignal().filter(x => x.category === NotificationCategory.Reports)));
  public assignmentsNotificationCountComputed = computed(() => this.getNotificationCount(this.notificationService.notificationsComputedSignal().filter(x => x.category === NotificationCategory.Assignments)));
  public alarmNotificationCountComputed = computed(() => this.getNotificationCount(this.notificationService.notificationsComputedSignal().filter(x => x.category === NotificationCategory.Alarms)));
  public integrationNotificationCountComputed = computed(() => this.getNotificationCount(this.notificationService.notificationsComputedSignal().filter(x => x.category === NotificationCategory.Integrations)));

  public unreadSystemNotificationCountComputed = computed(() => {
      const errorCount = this.notificationService.systemNotificationsComputedSignal()?.length ?? 0;
      const systemCount = this.notificationService.notificationsComputedSignal()
          ?.filter(x => x.category === NotificationCategory.System && !x.isRead)?.length ?? 0;
      const totalCount = errorCount + systemCount;

      return totalCount > 9 ? '9+' : totalCount.toString();
  });
  /**
   * Whether the System view has anything to list under the Only show unread toggle. Mirrors the list's own filter:
   * the session's errors always count, the system notifications only while unread when the toggle is on.
   */
  public hasVisibleSystemNotificationsComputed = computed(() => {
    const errorCount = this.notificationService.systemNotificationsComputedSignal()?.length ?? 0;
    const unreadOnly = this.showUnreadOnly();
    const systemCount = this.notificationService.notificationsComputedSignal()
      ?.filter(x => x.category === NotificationCategory.System && (!unreadOnly || !x.isRead))?.length ?? 0;
    return errorCount + systemCount > 0;
  });

  /** System's Mark all as read shows while it holds an unread system notification - the same rule as the records'. */
  public showSystemMarkAllAsReadComputed = computed(() =>
    this.notificationService.notificationsComputedSignal().some(x => x.category === NotificationCategory.System && !x.isRead),
  );

  /**
   * The category tab strip. Reviews and Assignments carry the Action Center's icons for the same lists; the rest
   * the glyph their rows use.
   *
   * System is not one of them: it is the panel's other view (see {@link view}, and the template for why).
   */
  public tabs = computed<NotificationTab[]>(() => [
    { value: '0', title: 'Reviews', icon: 'fa-regular fa-clipboard-check', count: this.reviewNotificationCountComputed(), dataId: 'review-count' },
    { value: '1', title: 'Assignments', icon: 'fa-regular fa-user-check', count: this.assignmentsNotificationCountComputed(), dataId: 'assignment-count' },
    { value: '2', title: 'Reports', icon: 'fa-regular fa-file-chart-column', count: this.reportNotificationCountComputed(), dataId: 'report-count' },
    { value: '3', title: 'Alarms', icon: 'fa-regular fa-siren-on', count: this.alarmNotificationCountComputed(), dataId: 'alarms-count' },
    { value: '4', title: 'Integrations', icon: 'fa-regular fa-plug', count: this.integrationNotificationCountComputed(), dataId: 'integration-count' },
  ]);

  /** The open category tab: the key the list @switch reads. */
  public activeTab = signal<string>('0');

  /**
   * Which view of the panel is showing: the officer's record notifications, or System. The administration center
   * has only System, so it opens there and shows no way between them.
   */
  public view = signal<'records' | 'system'>(this.environmentService.isAdministrationCenter ? 'system' : 'records');

  // The two paged lists, 25 a page by default.
  public reviewPage = signal(1);
  public reviewPageSize = signal(25);
  public assignmentPage = signal(1);
  public assignmentPageSize = signal(25);

  public showUnreadOnly = signal(false);
  public loading: boolean = false;
  public markAllLoading: boolean = false;
  public showMarkAllAsRead: boolean = false;
  public sidebarVisible: boolean = false;
  public skeletonListItems = Array(15).fill(0);

  /**
   * A computed rather than set in the grouping effect: the effect would then read the system notifications, and
   * every problem this session logs would regroup and re-render the officer's whole list.
   */
  public showBadge = computed(() => this.isNotificationFeatureEnabled() && this.notificationsCountComputed() !== '0');

  // private


  private notificationsEffect = effect(() => {
    if (!this.isNotificationFeatureEnabled()) return;

    if (this.authService.isAuthenticatedComputedSignal() && this.authService.userPrincipalComputedSignal()) {
      this.loadNotifications();
    }
  });

  private notificationDataEffect = effect(() => {
    if (!this.isNotificationFeatureEnabled()) return;

    let notifications = this.notificationService.notificationsComputedSignal();

    if (this.showUnreadOnly()) {
      notifications = notifications.filter(x => !x.isRead);
    }

    if (notifications) {
      this.groupedReviewNotifications = this.groupNotifications(notifications.filter(x => x.category == NotificationCategory.NarrativeReviews || x.category == NotificationCategory.IncidentReviews));
      this.groupedAssignmentNotifications = this.groupNotifications(notifications.filter(x => x.category == NotificationCategory.Assignments));
      this.groupedAlarmNotifications = this.groupNotifications(notifications.filter(x => x.category == NotificationCategory.Alarms));
      this.reportNotifications = notifications.filter(x => x.category == NotificationCategory.Reports);
      this.integrationNotifications = notifications.filter(x => x.category == NotificationCategory.Integrations);

      this.showMarkAllAsRead = notifications.some(x => x.isRead === false);

      // A shorter list (the unread filter, a mark-as-read) can leave the page past its end, which shows nothing.
      // CLAMPED rather than reset: a new notification arriving, or one being marked read, must not throw the
      // officer back to page 1 of a list they are working down.
      untracked(() => {
        this.clampPage(this.reviewPage, this.reviewPageSize(), this.groupedReviewNotifications.length);
        this.clampPage(this.assignmentPage, this.assignmentPageSize(), this.groupedAssignmentNotifications.length);
      });
    }
  });

  /**
   * Runs on every open. daisy-drawer has no opened event, and the panel is opened from more than the bell (the
   * service is public), so this follows the open state itself.
   */
  private showEffect = effect(() => {
    if (this.visible()) {
      untracked(() => this.onShowSidebar());
    }
  });


  /**
   * Fetch the officer's notifications. NOT WHILE OFFLINE: the request could only fail (the offline interceptor refuses
   * it), and the officer was shown an error for the device's state. The panel keeps what was last fetched, and the
   * next open once the connection is back fetches again. A link that is down without the browser knowing still fails
   * the request, and `showLoadError` keeps that quiet too.
   */
  public loadNotifications() {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      // Cleared here as the fetch's own `finalize` would: closing the panel set it, and it hides the lists.
      this.loading = false;
      return;
    }
    this.notificationService.getNotificationsForCurrentUser().pipe(finalize(() => this.loading = false)).subscribe({
      error: (error: unknown) => {
        this.toastMessageService.showLoadError("An error occurred retrieving notifications.", error)
      }
    })
  }

  public onToggleNotificationsPanelClick(): void {
    this.notificationService.showNotificationSidebar();
    this.loadNotifications();
  }

  public onHideSidebarClick() {
    this.notificationService.hideNotificationSidebar();
    this.loading = true;
  }

  private clampPage(page: WritableSignal<number>, pageSize: number, total: number): void {
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (page() > lastPage) {
      page.set(lastPage);
    }
  }

  /** One page of a list, for the two tabs that page (1-based, as daisy-pagination counts). */
  public pageOf<T>(items: T[], page: number, pageSize: number): T[] {
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }

  public groupNotifications(notifications: NotificationModel[]): NotificationGroupModel[] {
    if (!notifications || notifications.length === 0) {
      return [];
    }

    // Group by ModuleId using a Map for O(1) lookup
    const groupsMap = new Map<string | number, NotificationGroupModel>();

    notifications.forEach(notification => {
      // Use a fallback key for items without a module ID (e.g., 'general')
      const key = notification.relatedModule?.moduleId ?? 'general';

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          count: 0,
          notifications: [],
          latestDate: new Date(0) // Initialize with epoch
        });
      }

      const group = groupsMap.get(key)!;
      group.notifications.push(notification);
      group.count++;

      // Track the latest date in this group for sorting later
      const notifDate = new Date(notification.createdOn);
      if (group.latestDate && notifDate > group.latestDate) {
        group.latestDate = notifDate;
      }
    });

    // Convert Map to Array
    const groupedResult = Array.from(groupsMap.values());

    // Sort the GROUPS by their most recent notification (Newest first)
    groupedResult.sort((a, b) => {
      const dateA = a.latestDate?.getTime() ?? 0;
      const dateB = b.latestDate?.getTime() ?? 0;
      return dateB - dateA; // Descending sort
    });

    return groupedResult;
  }

  /**
   * Clears what the open view lists: every category from the records view, only the system notifications from System.
   * The session's errors need nothing here - opening System already marked them read.
   */
  onMarkAllAsReadClick() {
    this.markAllLoading = true;
    const inSystem = this.view() === 'system';
    const notifications = this.notificationService.notificationsComputedSignal()
      .filter(x => !inSystem || x.category === NotificationCategory.System);
    notifications.forEach(notification => {
      notification.isRead = true;
    });

    this.notificationService.updateNotificationsStatus(notifications).pipe(finalize(() => this.markAllLoading = false)).subscribe({
      error: () => {
        this.toastMessageService.showError('Error updating notification status.')
      }
    })
  }

  private getNotificationCount(notifications: NotificationModel[]): string {
    const unreadCount = notifications.filter(x => !x.isRead).length;

    return unreadCount > 9 ? "9+" : unreadCount.toString();
  }

  /** Picking a category also leaves System. */
  onTabChange(value: string) {
    this.activeTab.set(value);
    this.view.set('records');
  }

  /** Opening System marks what it holds as read. */
  onViewChange(view: 'records' | 'system') {
    this.view.set(view);
    if (view === 'system') {
      this.notificationService.markAllSystemNotificationsAsRead();
    }
  }

  onShowSidebar() {
    if (this.view() === 'system') {
      this.notificationService.markAllSystemNotificationsAsRead();
    }
  }
}
